import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../api';
import styles from '../styles/Auth.module.css';

const PasswordResetPage = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenUser, setTokenUser] = useState(null);
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if this is a Discord-initiated reset (has token in URL)
  const resetToken = searchParams.get('token');
  const isDiscordReset = !!resetToken;

  // Password strength requirements
  const requirements = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    number: /\d/.test(newPassword),
    special: /[!@#$%^&*]/.test(newPassword),
  };

  const getPasswordStrength = () => {
    const metRequirements = Object.values(requirements).filter(Boolean).length;
    if (metRequirements <= 2) return 'weak';
    if (metRequirements <= 4) return 'medium';
    return 'strong';
  };

  const validateResetToken = useCallback(async () => {
    console.log('Validating reset token:', resetToken);
    setIsLoading(true);
    setError('');

    try {
      const response = await api.validateResetToken(resetToken);
      console.log('Token validation response:', response.data);
      console.log('Response success:', response.data.success);
      console.log('Response user:', response.data.user);
      if (response.data.success) {
        console.log('Setting token as valid');
        setTokenValid(true);
        setTokenUser(response.data.user);
      } else {
        console.log('Setting token as invalid');
        setError('Invalid or expired reset link. Please request a new one.');
        setTokenValid(false);
      }
    } catch (err) {
      console.error('Token validation error:', err);
      setError('Invalid or expired reset link. Please request a new one.');
      setTokenValid(false);
    } finally {
      setIsLoading(false);
    }
  }, [resetToken]);

  // Validate Discord reset token on component mount
  useEffect(() => {
    console.log('PasswordResetPage useEffect:', { isDiscordReset, resetToken });
    if (isDiscordReset && resetToken) {
      console.log('Starting token validation...');
      validateResetToken();
    }
  }, [isDiscordReset, resetToken, validateResetToken]);

  // Track component lifecycle
  useEffect(() => {
    console.log('PasswordResetPage mounted');
    return () => {
      console.log('PasswordResetPage unmounting - this might indicate a redirect');
      console.log('Current URL when unmounting:', window.location.href);
    };
  }, []);



  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (Object.values(requirements).some(req => !req)) {
      setError('Please meet all password requirements');
      return;
    }

    setIsLoading(true);

    try {
      if (isDiscordReset) {
        // Discord-initiated reset
        await api.resetPasswordWithToken(resetToken, newPassword);
        setSuccess('Password reset successfully! Redirecting to login...');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        // Admin-required reset
        await api.resetPassword(newPassword);
        setSuccess('Password updated successfully! Redirecting to login...');
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while validating Discord token
  if (isDiscordReset && isLoading && !tokenValid) {
    console.log('Showing loading state');
    return (
      <div className={styles.authContainer}>
        <div className={styles.authCard}>
          <h2 className={styles.authTitle}>Validating Reset Link</h2>
          <p>Please wait while we validate your reset link...</p>
        </div>
      </div>
    );
  }

  // Show error if Discord token is invalid (only after validation has completed)
  if (isDiscordReset && !tokenValid && !isLoading && error) {
    console.log('Showing error state - token invalid');
    console.log('State values:', { isDiscordReset, tokenValid, isLoading, error });
    return (
      <div className={styles.authContainer}>
        <div className={styles.authCard}>
          <h2 className={styles.authTitle}>Invalid Reset Link</h2>
          <div className={styles.errorMessage}>{error}</div>
          <p>Please request a new password reset link.</p>
          <button
            onClick={() => navigate('/forgot-password')}
            className={styles.button}
          >
            Request New Reset Link
          </button>
        </div>
      </div>
    );
  }

  console.log('Rendering main password reset form');
  console.log('Final state values:', { isDiscordReset, tokenValid, isLoading, tokenUser });
  console.log('Current URL:', window.location.href);
  console.log('Component will render password reset form');

  return (
    <div className={styles.authContainer}>
      <div className={styles.authCard}>
        <h2 className={styles.authTitle}>
          {isDiscordReset ? 'Reset Password' : 'Set New Password'}
        </h2>

        {isDiscordReset && tokenUser && (
          <div className={styles.successMessage}>
            <p>Resetting password for: <strong>{tokenUser.username}</strong></p>
          </div>
        )}

        {error && <div className={styles.errorMessage}>{error}</div>}
        {success && <div className={styles.successMessage}>{success}</div>}

        <form onSubmit={handleSubmit} className={styles.authForm}>
          <div className={styles.formGroup}>
            <label htmlFor="newPassword" className={styles.label}>
              New Password
            </label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={styles.input}
              required
            />
            <div className={styles.strengthMeter}>
              <div
                className={`${styles.strengthIndicator} ${styles[getPasswordStrength()]}`}
              />
            </div>
            <div className={styles.requirements}>
              <div className={styles.requirementItem}>
                <span className={requirements.length ? styles.checkIcon : styles.xIcon}>
                  {requirements.length ? '✓' : '✗'}
                </span>
                {' '}At least 8 characters
              </div>
              <div className={styles.requirementItem}>
                <span className={requirements.uppercase ? styles.checkIcon : styles.xIcon}>
                  {requirements.uppercase ? '✓' : '✗'}
                </span>
                {' '}One uppercase letter
              </div>
              <div className={styles.requirementItem}>
                <span className={requirements.lowercase ? styles.checkIcon : styles.xIcon}>
                  {requirements.lowercase ? '✓' : '✗'}
                </span>
                {' '}One lowercase letter
              </div>
              <div className={styles.requirementItem}>
                <span className={requirements.number ? styles.checkIcon : styles.xIcon}>
                  {requirements.number ? '✓' : '✗'}
                </span>
                {' '}One number
              </div>
              <div className={styles.requirementItem}>
                <span className={requirements.special ? styles.checkIcon : styles.xIcon}>
                  {requirements.special ? '✓' : '✗'}
                </span>
                {' '}One special character (!@#$%^&*)
              </div>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="confirmPassword" className={styles.label}>
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={styles.input}
              required
            />
          </div>

          <button
            type="submit"
            className={styles.button}
            disabled={!Object.values(requirements).every(Boolean) || !confirmPassword || isLoading}
          >
            {isLoading ? 'Resetting...' : (isDiscordReset ? 'Reset Password' : 'Update Password')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PasswordResetPage;
